// PPS CRM - Surveys Module
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

interface Survey {
  id: string;
  title: string;
  weekOf: string;
  introText?: string;
  status: 'DRAFT' | 'ACTIVE' | 'CLOSED';
  _count?: { responses: number };
}

export default function Surveys() {
  const { user } = useAuthStore();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSurveyId, setLinkSurveyId] = useState<string | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');

  useEffect(() => {
    fetchSurveys();
    fetchClients();
  }, []);

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/api/surveys');
      setSurveys(response.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const publishSurvey = async (id: string) => {
    try {
      await api.patch(`/api/surveys/${id}/publish`);
      fetchSurveys();
    } catch (error) {
      console.error('Error publishing survey:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get('/api/clients');
      setClients(res.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const generateLink = async () => {
    if (!linkSurveyId || !selectedClient) return;
    try {
      const res = await api.post(`/api/surveys/${linkSurveyId}/link`, { clientId: selectedClient });
      setGeneratedLink(res.data.url || '');
    } catch (error) {
      console.error('Error generating survey link:', error);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Surveys</h2>
        {(user?.role === 'OWNER' || user?.role === 'MANAGER') && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            + New Survey
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : surveys.length === 0 ? (
          <div className="p-8 text-center text-dark-gray">No surveys found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-light-gray">
              <tr>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Week Of</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Responses</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id} className="border-b border-light-gray hover:bg-light-gray">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.title}</div>
                    {s.introText && <div className="text-sm text-dark-gray">{s.introText}</div>}
                  </td>
                  <td className="px-4 py-3">{new Date(s.weekOf).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{s.status}</td>
                  <td className="px-4 py-3">{s._count?.responses || 0}</td>
                  <td className="px-4 py-3">
                    {s.status === 'DRAFT' && (user?.role === 'OWNER' || user?.role === 'MANAGER') && (
                      <button
                        onClick={() => publishSurvey(s.id)}
                        className="text-primary hover:underline text-sm"
                      >
                        Publish
                      </button>
                    )}
                    {s.status === 'ACTIVE' && (
                      <button
                        onClick={() => {
                          setGeneratedLink('');
                          setSelectedClient('');
                          setLinkSurveyId(s.id);
                          setShowLinkModal(true);
                        }}
                        className="text-primary hover:underline text-sm ml-4"
                      >
                        Send Link
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <SurveyModal
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchSurveys();
          }}
        />
      )}

      {showLinkModal && linkSurveyId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">Send Survey Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullLegalName}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={generateLink}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
                disabled={!selectedClient}
              >
                Generate Link
              </button>
              {generatedLink && (
                <div className="p-3 bg-light-gray rounded">
                  <div className="text-xs text-dark-gray mb-2">Survey Link</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedLink}
                      className="w-full px-3 py-2 border border-mid-gray rounded"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(generatedLink)}
                      className="bg-primary text-white px-3 py-2 rounded hover:bg-primary-dark"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SurveyModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [title, setTitle] = useState('');
  const [weekOf, setWeekOf] = useState(new Date().toISOString().split('T')[0]);
  const [introText, setIntroText] = useState('');
  const [questions, setQuestions] = useState([{ questionText: '', questionType: 'SHORT_TEXT', required: true }]);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/surveys', {
        title,
        weekOf,
        introText,
        questions: questions.filter((q) => q.questionText.trim()),
      });
      onSave();
    } catch (error) {
      console.error('Error creating survey:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-xl font-bold mb-4">New Survey</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Week Of</label>
            <input
              type="date"
              value={weekOf}
              onChange={(e) => setWeekOf(e.target.value)}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Intro Text</label>
            <textarea
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Questions</label>
            {questions.map((q, idx) => (
              <input
                key={idx}
                type="text"
                placeholder={`Question ${idx + 1}`}
                value={q.questionText}
                onChange={(e) => {
                  const next = [...questions];
                  next[idx] = { ...next[idx], questionText: e.target.value };
                  setQuestions(next);
                }}
                className="w-full px-3 py-2 border border-mid-gray rounded mb-2"
              />
            ))}
            <button
              type="button"
              onClick={() => setQuestions([...questions, { questionText: '', questionType: 'SHORT_TEXT', required: true }])}
              className="text-sm text-primary hover:underline"
            >
              + Add Question
            </button>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
